import { useTranslation } from "react-i18next";
import {
  Card,
  Progress,
  Skeleton,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from "@h2j-data-team/ui";
import { formatMeasurementProgress } from "@/lib/qlik-evaluation-format";

const COLUMNS = 7;

// A skeleton of the completed analysis result (live progress + filter bar +
// table), shown while the analysis is processing so the page keeps the same
// shape rather than swapping to a different layout.
export function AnalysisResultSkeleton({
  progress,
  rows = 10,
}: {
  progress?: { currentItem?: string | null; completed: number; total: number } | null;
  rows?: number;
}) {
  const { t } = useTranslation("analyses");
  const pct =
    progress && progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  return (
    <div className="space-y-4" aria-busy="true">
      <p role="status" className="sr-only">
        {t("lifecycle.processingTitle")}
      </p>

      {/* live progress — replaces the metadata line while the run is active */}
      {progress && (
        <Card className="gap-0 px-5 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="truncate text-[13.5px] font-semibold text-ink">
              {progress.currentItem
                ? formatMeasurementProgress(progress.currentItem, t)
                : t("lifecycle.processingDescription")}
            </span>
            <span className="shrink-0 font-mono text-[12.5px] font-medium text-dim tabular-nums">
              {progress.completed}/{progress.total}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Progress
              value={pct}
              className="flex-1"
              aria-label={t("lifecycle.progress")}
              aria-valuetext={`${progress.completed}/${progress.total}`}
            />
            <span aria-hidden className="shrink-0 font-mono text-[12px] text-faint tabular-nums">
              {pct}%
            </span>
          </div>
        </Card>
      )}

      <div className="overflow-hidden rounded-lg border border-hairline bg-surface shadow-sm">
        {/* filter bar: search + filters */}
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-3">
          <Skeleton className="h-8 min-w-60 flex-1" />
          <Skeleton className="h-7 w-52" />
          <Skeleton className="h-7 w-56" />
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              {Array.from({ length: COLUMNS }).map((_, i) => (
                <TableHead key={i}>
                  <Skeleton className="h-3 w-12" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableSkeletonRows columns={COLUMNS} rows={rows} />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
